import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Users, AlertTriangle } from "lucide-react";

const PAIN_COLOR = {
  tightness:  "#FCD34D",
  soreness:   "#FB923C",
  discomfort: "#F87171",
  sharp_pain: "#DC2626",
};

/**
 * Team-Wide Body Scan Heatmap (Coach Dashboard).
 *
 * Silhouette aggregating ALL roster body-scan flags over the last 14 days,
 * surfacing patterns the coach should address at morning standup.
 */
export default function TeamBodyScanHeatmap({ days = 14 }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/dashboard/body-scan-heatmap?days=${days}`).then(({ data }) => setData(data));
  }, [days]);

  if (!data) return null;
  const maxSev = Math.max(...data.zones.map((z) => z.severity), 1);
  const sharpTotal = data.zones.reduce((s, z) => s + (z.sharp_pain_count || 0), 0);

  return (
    <div className="rounded-xl ink-card p-5" data-testid="team-body-scan-heatmap">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">Team Body Scan · last {data.days}d</div>
          <h3 className="font-display font-bold text-lg text-white mt-1">
            {data.total_flags} flag{data.total_flags === 1 ? "" : "s"} across {data.roster_size} athletes
          </h3>
        </div>
        {sharpTotal > 0 && (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide"
            style={{ backgroundColor: "rgba(220,38,38,0.18)", color: "#FCA5A5", border: "1px solid rgba(220,38,38,0.45)" }}>
            <AlertTriangle className="h-3 w-3" /> {sharpTotal} sharp pain
          </span>
        )}
      </div>

      {data.total_flags === 0 ? (
        <div className="text-xs text-white/50 py-6 text-center">
          No flagged zones across the roster in the last {data.days} days. 🟢
        </div>
      ) : (
        <div className="grid grid-cols-[110px_1fr] gap-3 items-start">
          <svg viewBox="0 0 240 520" className="w-full h-auto block">
            <g fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth="1.2">
              <circle cx="120" cy="48" r="28" />
              <path d="M108 76 L108 92 L132 92 L132 76" />
              <path d="M70 100 L170 100 L182 200 L168 295 L72 295 L58 200 Z" />
              <path d="M70 100 L40 200 L46 260 L60 260 L72 200" />
              <path d="M170 100 L200 200 L194 260 L180 260 L168 200" />
              <path d="M88 295 L78 420 L84 500 L106 500 L112 420 L116 305" />
              <path d="M152 295 L162 420 L156 500 L134 500 L128 420 L124 305" />
            </g>
            {data.zones.map((z) => {
              const ratio = z.severity / maxSev;
              const r = 5 + Math.round(ratio * 9);
              const color = PAIN_COLOR[z.max_level] || "#22D3EE";
              return (
                <g key={z.zone_id} data-testid={`team-heatmap-zone-${z.zone_id}`}>
                  <circle cx={z.coords.x} cy={z.coords.y} r={r} fill={color} fillOpacity={0.35 + ratio * 0.4} stroke={color} strokeWidth="1.5" />
                  {z.sharp_pain_count > 0 && (
                    <circle cx={z.coords.x} cy={z.coords.y} r={r + 3} fill="none" stroke={PAIN_COLOR.sharp_pain} strokeWidth="1.2" strokeDasharray="2,2">
                      <animate attributeName="r" values={`${r + 3};${r + 7};${r + 3}`} dur="2s" repeatCount="indefinite" />
                    </circle>
                  )}
                </g>
              );
            })}
          </svg>

          <div className="space-y-1.5">
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/40">Top hot zones</div>
            {data.zones.slice(0, 4).map((z) => {
              const color = PAIN_COLOR[z.max_level] || "#22D3EE";
              return (
                <div key={z.zone_id} className="rounded-lg px-2.5 py-1.5 bg-white/[0.03] border border-white/10"
                  data-testid={`team-heatmap-row-${z.zone_id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className="inline-block w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                      <span className="font-semibold text-white text-xs truncate">{z.zone_label}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-[10px] text-white/60">
                      <Users className="h-3 w-3" />
                      <span className="font-mono-num">{z.affected_players}</span>
                      {z.sharp_pain_count > 0 && (
                        <span className="ml-1 font-bold" style={{ color: PAIN_COLOR.sharp_pain }}>· {z.sharp_pain_count}⚠</span>
                      )}
                    </div>
                  </div>
                  {z.affected_player_names?.length > 0 && (
                    <div className="text-[10px] text-white/45 mt-0.5 truncate">
                      {z.affected_player_names.slice(0, 4).join(" · ")}
                      {z.affected_players > 4 && ` +${z.affected_players - 4}`}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
